// ════════════════════════════════════════════════════════════════════
// W'ILY'S PIER — mineriumCompanion.jsx   ·   THE COMPANION ADAPTER
// ────────────────────────────────────────────────────────────────────
// ⭐ THEY OWN THE BODY, WE OWN THE POSE.
//
// Quoted from `app-rush/src/racingBeingAdapter.jsx`, which had this
// right first and which `gamemaster/MODELS.md` §2 holds up as the
// pattern to copy:
//
//     `ieBeingModels.jsx` is CROSS-GAME CANONICAL. It is never
//     edited here. Not one line, not one colour, not one number.
//     If a being looks wrong in a kart, the fix lands in THIS file
//     or it doesn't land. That is the entire anti-drift bargain:
//     they own the body, we own the pose.
//
// Rush seats a being in a kart. Annihilation stands it in a field.
// Pierre walks it around your holding a step behind you. Those are
// three POSES of one BODY, and the difference lives in three adapter
// files — never in the kit. ⚠ If a companion ever looks wrong here, the
// fix goes in this file or it does not go in.
//
// ── THE LAW OF ONE BODY (MODELS.md §1) ─────────────────────────────
// A being has ONE 3D model in the Illegal Empire. Not one per game —
// one. Pierre's copies of `ieBeingModels.jsx` and `ieTerrorSpecies.jsx`
// are VENDORED, byte-identical to `gamemaster/models/`, and are never
// edited locally. A Pierre that is BEHIND the master is fine and
// expected ("staleness is fine, silent forking is not"); a Pierre that
// has edited its own copy sideways is the fork this law exists to stop.
//
// ── THE ROSTER SOURCE (MODELS.md §5) ────────────────────────────────
// caP'n, this session: "companions here will only use the Toonz from
// the obsidian vault, aka the character models in Annihilation and
// Rush."
//
// So the roster is exactly the two canon kits and nothing else:
//   · 37 vault species from `ieTerrorSpecies.jsx` (the Toonz)
//   · 12 core lineages from `ieBeingModels.jsx`, each at three tiers
// ⚠ CARDS ONLY. V'ael, Willie and the other NPCs in the Toonz folder
// are not cards and are never characters or races. They are not in
// either kit, which is why this file cannot accidentally offer one.
//
// ── ⚠ AND NOTHING IS ADDED FOR "CONSISTENCY" ───────────────────────
// THE NO-FORCED-CONSISTENCY LAW (MODELS.md §4b): "Consistency means the
// same being looks the same. It does NOT mean every game gets every
// being." Pierre gets these because caP'n asked for companions drawn
// from the Toonz, which is a design call he made — not because
// Annihilation has them.
// ════════════════════════════════════════════════════════════════════

import { useRef, useMemo } from "react";
import { useFrame } from "@react-three/fiber";
import {
  IEBeing, LINEAGE_CARD, LINEAGE_IDS, TIERS, getBeingPalette,
} from "./ieBeingModels";
import { FOUNDING_COMPANION } from "./mineriumCatalogue";

// ════════════════════════════════════════════════════════════════════
// ⚠⚠ THE 37 VAULT SPECIES ARE PARKED — AND THE FAULT IS NOT PIERRE'S
// ────────────────────────────────────────────────────────────────────
// `gamemaster/models/ieTerrorSpecies.jsx` DOES NOT COMPILE. Three
// identifiers are used and never declared anywhere in the file:
//
//   LYR    — the Lyraei body's palette   (used at lines 2380–2442)
//   KRK    — the Kraalok body's palette  (used at lines 3354–3412)
//   CROWN  — the crown-shape lookup      (used at line 4364:
//            `const draw = CROWN[idiom] || CROWN.points;`)
//
// ⚠ AND `app-annihilation/src/ieTerrorSpecies.jsx` IS BYTE-IDENTICAL TO
// THAT MASTER — md5 `0ab5a018…`. So this is not Pierre's copy going
// stale or sideways; the bug is in the master and is LATENT in every
// app carrying the file. Pierre is simply the first one to have
// compiled it. (`ie-cowork-workflow`: *check the master itself, not
// just the consumers* — this is that case, found the hard way.)
//
// They are NOT typos of anything nearby: `CROWN_Y` at line 4288 is a
// per-species height table, a different thing entirely. Two are
// per-body palettes and one is a shape table, and writing them means
// AUTHORING CONTENT IN A CROSS-GAME CANONICAL FILE. That is caP'n's
// call, not V'ael's, and patching Pierre's vendored copy instead would
// be exactly the silent FORK THE LAW OF ONE BODY exists to prevent.
//
// So the import is parked, not worked around. The file still sits in
// `src/` untouched and byte-identical — CRA lints and compiles only
// what the module graph reaches, so an unimported file costs nothing.
//
// ── TO TURN THE 37 BACK ON, once the master declares those three ────
//   1 · re-vendor `gamemaster/models/ieTerrorSpecies.jsx` into `src/`
//   2 · uncomment the import below
//   3 · flip SPECIES_KIT to true
// Nothing else in this file, or any other, has to change.
// ════════════════════════════════════════════════════════════════════
// import {
//   TerrorSpecies, SPECIES_CARD, SPECIES_IDS, getSpeciesPalette, hasSpecies,
// } from "./ieTerrorSpecies";

export const SPECIES_KIT = false;

export { TIERS };

// ── THE ROSTER ──────────────────────────────────────────────────────
// One list, two kits, one shape — so a picker reads 49 beings the same
// way and never has to know which file a body came out of.
export const COMPANIONS = (() => {
  const out = [];
  for (const id of LINEAGE_IDS) {
    const c = LINEAGE_CARD[id];
    out.push({
      id, kit: "lineage", name: c.name, ilk: c.ilk, cls: c.cls,
      lyf: c.lyf, lyt: c.lyt, flavor: c.flavor || "", tiers: ["beb", "champion", "lord"],
      pal: getBeingPalette(id),
    });
  }
  // eslint-disable-next-line no-constant-condition
  if (SPECIES_KIT) {
    // Restored in one block when the master compiles again. Left as
    // real code rather than deleted so turning it back on is a flag and
    // not an archaeology exercise.
    /* eslint-disable no-undef */
    // for (const id of SPECIES_IDS) {
    //   const c = SPECIES_CARD[id];
    //   out.push({
    //     id, kit: "species", name: c.name, ilk: c.ilk, cls: c.cls,
    //     lyf: c.lyf, lyt: c.lyt, flavor: c.flavor || "", trad: c.trad || "",
    //     tiers: ["beb", "champion", "lord"],
    //     pal: getSpeciesPalette(id),
    //   });
    // }
    /* eslint-enable no-undef */
  }
  return out;
})();

export const COMPANION_BY_ID = Object.fromEntries(COMPANIONS.map((c) => [c.id, c]));
export const COMPANION_IDS = COMPANIONS.map((c) => c.id);
// ⚠ A CORE-CAST BEING while the species kit is parked. `xolibrix` is a
// vault species and would resolve to nothing today — a default that
// points at an absent body is an empty picker with no error.
export const DEFAULT_COMPANION = FOUNDING_COMPANION;

export const companionCard = (id) => COMPANION_BY_ID[id] || null;

// ── ⚠ THE UNLOCK GATE, DELIBERATELY OPEN ───────────────────────────
// caP'n's standing preference on a big build: ship the scaffolding
// first so the seams can be proven to function, then fill in the
// content. This is that seam, and it is FLAGGED rather than hidden:
// right now every being in both kits is pickable.
//
// When companions should be earned — from a Terror collection, an
// achievement, a Bazaar piece, a quest — this ONE function is the only
// thing that changes, and every picker, every renderer and every write
// path already asks it. ⚑ It must stay the only gate; a second check
// somewhere else is how a being becomes pickable in the menu and
// refused at the write.
export function companionUnlocked(/* uid, beingId */) {
  return true;
}

// ════════════════════════════════════════════════════════════════════
// THE POSE
// ────────────────────────────────────────────────────────────────────
// The kits render a being standing in its neutral pose, alive and
// idling, at the origin, facing +Z, roughly one unit tall. A Minerium
// tile is one unit and the player sprite stands 1.5 tiles, so a
// companion at 0.78 reads as a creature at your heel rather than a
// second person walking beside you.
//
// ⚠ SCALE LIVES IN A WRAPPER GROUP, NEVER IN THE BODY. "Make it bigger"
// is one number here; touching the kit's internals would break the
// footprint contract every other title depends on.
// ════════════════════════════════════════════════════════════════════
export const COMPANION_SCALE = 0.78;

// Tier changes what a being IS, not how big it is (ASCENSION IS
// STATION, NOT SCALE) — but a Lord that stood the same height as its
// Beb would read wrong beside a player, so the wrapper gives each tier
// a modest lift. This is a POSE decision and belongs here.
const TIER_SCALE = { beb: 1, champion: 1.16, lord: 1.32 };

export function CompanionBody({ beingId, tier = "beb" }) {
  const card = COMPANION_BY_ID[beingId];
  if (!card) return null;
  const s = COMPANION_SCALE * (TIER_SCALE[tier] || 1);
  // While SPECIES_KIT is false the roster only ever holds lineages, so
  // this is the one branch there is. The species branch returns with the
  // import — see the banner at the top of the file.
  return (
    <group scale={[s, s, s]}>
      <IEBeing being={beingId} tier={tier} />
    </group>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE FOLLOW
// ────────────────────────────────────────────────────────────────────
// It trails you at a fixed distance and turns to face where it is
// going. ⚠ EVERYTHING HERE READS A REF INSIDE useFrame — there is not
// one piece of React state in this component, because a companion that
// re-rendered as you walked would reconcile the whole 3D tree at
// walking speed (ie-realtime-perf Cause 1: MOVEMENT NEVER RE-RENDERS).
// ════════════════════════════════════════════════════════════════════
export function Companion({ beingId, tier = "beb", posRef, offset = 1.05 }) {
  const g = useRef();
  const yaw = useRef(0);
  const goal = useRef({ x: 0, z: 0 });
  const started = useRef(false);

  const card = COMPANION_BY_ID[beingId];
  const glow = card?.pal?.glow || "#e8d8a8";

  // Captured once. r3f re-applies a `position` prop on every render, so
  // pointing it at a value the frame loop mutates would snap the
  // companion back to wherever the last render happened to leave it.
  const initPos = useMemo(() => {
    const p = posRef?.current || { x: 0, y: 0 };
    return [p.x - offset, 0, p.y + offset];
  }, [posRef, offset]);

  useFrame((_, dt) => {
    const o = g.current; if (!o || !posRef?.current) return;
    const p = posRef.current;
    const d = Math.min(dt || 0.016, 0.05);

    // Only re-target when the player has actually walked away, so the
    // companion settles instead of jittering around a standing player.
    const dx = p.x - o.position.x, dz = p.y - o.position.z;
    const dist = Math.hypot(dx, dz);
    if (!started.current || dist > offset * 1.35) {
      started.current = true;
      const k = dist > 0.001 ? offset / dist : 0;
      goal.current.x = p.x - dx * k;
      goal.current.z = p.y - dz * k;
    }

    const s = 1 - Math.pow(0.0015, d);
    const nx = o.position.x + (goal.current.x - o.position.x) * s;
    const nz = o.position.z + (goal.current.z - o.position.z) * s;

    const mx = nx - o.position.x, mz = nz - o.position.z;
    if (Math.hypot(mx, mz) > 0.0008) {
      const want = Math.atan2(mx, mz);
      // shortest-arc turn, so it never spins the long way round
      let diff = want - yaw.current;
      while (diff > Math.PI) diff -= Math.PI * 2;
      while (diff < -Math.PI) diff += Math.PI * 2;
      yaw.current += diff * Math.min(1, d * 7);
      o.rotation.y = yaw.current;
    }
    o.position.x = nx;
    o.position.z = nz;
  });

  if (!card) return null;
  return (
    <group ref={g} position={initPos}>
      {/* the shadow that keeps it ON the ground rather than above it */}
      <mesh position={[0, 0.015, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.34, 14]} />
        <meshBasicMaterial color="#000" transparent opacity={0.36} depthWrite={false} />
      </mesh>
      {/* a whisper of its own palette on the ground, so a dark being
          on dark moss still reads as being somewhere. Emissive, basic,
          zero lights — the yard's light count is fixed at three. */}
      <mesh position={[0, 0.02, 0]} rotation={[-Math.PI / 2, 0, 0]}>
        <circleGeometry args={[0.5, 14]} />
        <meshBasicMaterial color={glow} transparent opacity={0.07} depthWrite={false} toneMapped={false} />
      </mesh>
      <CompanionBody beingId={beingId} tier={tier} />
    </group>
  );
}

// A being standing still on a plinth, for the picker and the codex.
// Same body, no follow — the pose is the only difference, which is the
// whole point of the adapter pattern.
export function CompanionPortrait({ beingId, tier = "beb", spin = true }) {
  const g = useRef();
  useFrame(({ clock }) => { if (spin && g.current) g.current.rotation.y = clock.elapsedTime * 0.45; });
  return (
    <group ref={g}>
      <CompanionBody beingId={beingId} tier={tier} />
    </group>
  );
}

// ── THE SCRIPTED ROSTER CHECK ───────────────────────────────────────
// Every id in the roster must resolve to BOTH a card AND a body, so the
// list and the kits cannot silently diverge. Exactly the check
// `ie-being-models` §7 asks for, and the reason it exists is that a
// picker offering a being with no body is invisible until somebody
// picks it.
export function auditCompanions() {
  const problems = [];
  for (const c of COMPANIONS) {
    if (!c.name) problems.push(`${c.id}: no name on its card`);
    if (!c.pal || !c.pal.main) problems.push(`${c.id}: no palette`);
  }
  if (!SPECIES_KIT) {
    // Not a problem — a NOTICE, so the shortfall is visible at boot
    // rather than being discovered as "why are there only twelve?"
    problems.push(
      "NOTICE: the 37 vault species are parked — gamemaster/models/ieTerrorSpecies.jsx " +
      "does not compile (LYR, KRK and CROWN are used and never declared). " +
      "12 core lineages are live. See the banner in mineriumCompanion.jsx."
    );
  }
  if (!COMPANION_BY_ID[DEFAULT_COMPANION]) problems.push(`DEFAULT_COMPANION "${DEFAULT_COMPANION}" is not in the roster`);
  return problems;
}

export default Companion;
